import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { WebSearchProduct } from "@/api/models/api-types";
import type { ProductComparisonSnapshot } from "@/features/product-comparison/model";
import type { ChatAttachment } from "../types";
import { createUuid } from "@/lib/uuid";

// ===== Streaming Store Types =====
export interface StreamingError {
  message: string;
  code?: string;
  timestamp: number;
}

export interface StreamingStatus {
  content: string;
  timestamp: number;
}

export interface StreamingReasoning {
  content: string;
  timestamp: number;
}

export interface StreamingExternalLink {
  url: string;
  label: string;
  timestamp: number;
}

export type StreamingSessionStatus =
  | "idle"
  | "streaming"
  | "completed"
  | "error";

// Aktif stream sürerken gönderilen ve sırasını bekleyen kullanıcı mesajı.
// Stream tamamlanınca FIFO sırayla otomatik gönderilir.
export interface QueuedChatMessage {
  id: string;
  message: string;
  attachments: ChatAttachment[];
  // Optimistic önizleme için görsel attachment URL'leri
  attachmentImageUrls: string[];
  model?: string;
  timestamp: number;
}

export const PLAYGROUND_STREAM_KEY = "__playground__";

// İlk mesajın cevabı gelene kadar oturumun anahtarı. Wire'a chatId olarak
// gitmez. Her ilk mesaj kendi anahtarını alır: cevap beklenirken başka bir
// yeni sohbet açılırsa iki taslak aynı oturumu paylaşıp birbirini iptal etmesin.
const DRAFT_CHAT_PREFIX = "__draft__:";
// `/chat/:id` içinde konuşma olmayan yer tutucu: `/chat/draft-<nonce>`
const DRAFT_ROUTE_PREFIX = "draft-";

export const createDraftChatKey = (): string =>
  `${DRAFT_CHAT_PREFIX}${createUuid()}`;

export const isDraftChatKey = (key: string | null | undefined): key is string =>
  typeof key === "string" && key.startsWith(DRAFT_CHAT_PREFIX);

export const draftChatKeyFromRouteId = (
  routeId: string | null | undefined,
): string | null =>
  typeof routeId === "string" &&
  routeId.startsWith(DRAFT_ROUTE_PREFIX) &&
  routeId.length > DRAFT_ROUTE_PREFIX.length
    ? `${DRAFT_CHAT_PREFIX}${routeId.slice(DRAFT_ROUTE_PREFIX.length)}`
    : null;

export const draftRoutePath = (draftKey: string): string =>
  `/chat/${DRAFT_ROUTE_PREFIX}${draftKey.slice(DRAFT_CHAT_PREFIX.length)}`;

// ===== Session GC / Eviction =====
// Keep this conservative: never evict active streams or unread sessions.
const DEFAULT_MAX_SESSIONS = 75;
const DEFAULT_MAX_SESSION_AGE_MS = 1000 * 60 * 60 * 2; // 2 hours

type PruneOptions = {
  maxSessions?: number;
  maxAgeMs?: number;
};

export const hasCompletedDraft = (s: StreamingSession): boolean =>
  Boolean(
    s.completedServerMessageId ||
      s.completedUserMessageId ||
      s.completedUserMessage ||
      s.completedAssistantMessageId ||
      s.completedAssistantContent ||
      s.completedExternalLinks.length > 0 ||
      s.completedWebProducts.length > 0 ||
      s.completedWebQuery
  );

/** Yalnız history'deki kesin çiftle eşleştirilebilen draft yeni turu bekletir. */
export const hasIdentifiedCompletedDraft = (s: StreamingSession): boolean =>
  s.completedServerMessageId !== null &&
  s.completedAssistantMessageId !== null;

const isEvictableSession = (s: StreamingSession): boolean => {
  // Never evict playground session (it behaves like a singleton workspace).
  if (s.chatKey === PLAYGROUND_STREAM_KEY) return false;
  // Never evict active/in-flight sessions.
  if (s.status === "streaming") return false;
  if (s.abortController) return false;
  if (s.isConnected) return false;
  // Never evict unread (sidebar dot) sessions.
  if (s.hasUnread) return false;
  // Never evict if there is any optimistic UI waiting for backend sync.
  if (s.pendingUserMessage) return false;
  if (hasCompletedDraft(s)) return false;
  // Never evict sessions with queued (not yet sent) user messages.
  if (s.queuedMessages.length > 0) return false;
  return true;
};

const pruneSessionsByChat = (
  sessionsByChat: Record<string, StreamingSession>,
  options?: PruneOptions
): Record<string, StreamingSession> => {
  const maxSessions = options?.maxSessions ?? DEFAULT_MAX_SESSIONS;
  const maxAgeMs = options?.maxAgeMs ?? DEFAULT_MAX_SESSION_AGE_MS;
  const now = Date.now();

  const entries = Object.entries(sessionsByChat);
  if (entries.length <= maxSessions) {
    // Still remove very old idle sessions even if under max count.
    const next: Record<string, StreamingSession> = {};
    for (const [key, s] of entries) {
      const isTooOld = now - s.updatedAt > maxAgeMs;
      if (isTooOld && isEvictableSession(s)) continue;
      next[key] = s;
    }
    return next;
  }

  // 1) Age-based eviction for evictable sessions.
  const survivors: Array<[string, StreamingSession]> = [];
  const evictableCandidates: Array<[string, StreamingSession]> = [];

  for (const [key, s] of entries) {
    if (!isEvictableSession(s)) {
      survivors.push([key, s]);
      continue;
    }
    const isTooOld = now - s.updatedAt > maxAgeMs;
    if (isTooOld) {
      continue; // evict
    }
    evictableCandidates.push([key, s]);
  }

  const combined = [...survivors, ...evictableCandidates];
  if (combined.length <= maxSessions) {
    return Object.fromEntries(combined);
  }

  // 2) Count-based eviction (evict oldest evictable sessions first).
  evictableCandidates.sort((a, b) => a[1].updatedAt - b[1].updatedAt);
  const remainingSlots = Math.max(0, maxSessions - survivors.length);
  const keptEvictables = evictableCandidates.slice(
    Math.max(0, evictableCandidates.length - remainingSlots)
  );

  return Object.fromEntries([...survivors, ...keptEvictables]);
};

// Model Suggestions Types
export interface ModelSuggestionBrand {
  _id: string;
  title: string;
  _v?: number;
  thumbnail?: {
    type?: string;
    size?: number;
    folder?: string;
    fileUrl?: string;
  };
}

export interface ModelSuggestion {
  createdAt: string;
  updatedAt: string;
  id: string;
  title: string;
  name: string;
  brand: ModelSuggestionBrand | null;
  provider: string;
  provider_type: any;
  supported_tasks: string[];
  supported_subtasks: string[];
  categories: any;
  subcategories: any;
  complexity_level: number;
  isDeleted: boolean;
  max_tokens: number;
  context_window: number;
  supports_streaming: boolean;
  request_schema: Record<string, any>;
  response_schema: Record<string, any>;
  pricing: any;
  avg_response_time_ms: number;
  metadata: Record<string, any>;
  thumbnail_url: string;
  description: {
    tr: string;
    en: string;
    de: string;
    fr: string;
    es: string;
    ar: string;
  };
  output_object: any;
  attributes: any[];
  thumbnail: any;
}

export interface StreamingSession {
  // Session identity
  chatKey: string;

  /**
   * Aktif stream kuşağı. `startStreaming` her turda yenisini üretir.
   *
   * Aynı sohbete ait eski bir gönderim zinciri — ağdan geç dönen bir cevap,
   * iptal edilmemiş bir okuma döngüsü — kendi kuşağı artık geçerli değilken
   * oturuma yazamasın diye var. Store'a yazan her sonlandırma bu token'ı
   * doğruluyor; eşleşmiyorsa çağrı sessizce düşüyor.
   */
  streamId: string | null;

  // Status
  status: StreamingSessionStatus;

  // IDs
  streamingMessageId: string | null; // client-generated assistant message id (UI key)
  serverMessageId: string | null; // initial_response _id (backend message id)
  pendingUserMessageId: string | null; // client-generated user optimistic id

  // Content
  streamingContent: string;
  statusMessages: StreamingStatus[];
  reasoningMessages: StreamingReasoning[];
  reasoningContent: string;
  externalLinks: StreamingExternalLink[];
  /** Bu turun göstermesini istediği karşılaştırma. Yalnızca id'ler. */
  comparison: ProductComparisonSnapshot | null;
  webProducts: WebSearchProduct[];
  webSearchQuery: string | null;
  pendingUserMessage: string | null;
  // Optimistic user mesajına ait görsel attachment URL'leri
  // (backend refetch'i gelene kadar önizleme için)
  pendingUserAttachments: string[];

  // ===== Completed optimistic draft (waiting backend sync) =====
  completedServerMessageId: string | null;
  completedUserMessageId: string | null;
  completedUserMessage: string | null;
  completedUserAttachments: string[];
  completedAssistantMessageId: string | null;
  completedAssistantContent: string;
  /**
   * Stream sırasında gelen ve tamamlanmış draft'la birlikte ekranda tutulan
   * artefaktlar. History'ye metinden daha geç düşebiliyorlar; taşınmazlarsa
   * cevap "geldi" sayılıp draft silindiğinde bağlantılar ve web kartları
   * ekrandan kayboluyordu.
   */
  completedExternalLinks: StreamingExternalLink[];
  completedComparison: ProductComparisonSnapshot | null;
  completedWebProducts: WebSearchProduct[];
  completedWebQuery: string | null;

  // ===== Queued messages (stream sürerken gönderilenler) =====
  queuedMessages: QueuedChatMessage[];

  // Connection
  isConnected: boolean;
  abortController: AbortController | null;

  // UX flags
  hasUnread: boolean; // unread dot for sidebar
  updatedAt: number;

  // Error
  streamingError: StreamingError | null;
}

const createInitialSession = (chatKey: string): StreamingSession => ({
  chatKey,
  streamId: null,
  status: "idle",
  streamingMessageId: null,
  serverMessageId: null,
  pendingUserMessageId: null,
  streamingContent: "",
  statusMessages: [],
  reasoningMessages: [],
  reasoningContent: "",
  externalLinks: [],
  comparison: null,
  webProducts: [],
  webSearchQuery: null,
  pendingUserMessage: null,
  pendingUserAttachments: [],
  completedServerMessageId: null,
  completedUserMessageId: null,
  completedUserMessage: null,
  completedUserAttachments: [],
  completedAssistantMessageId: null,
  completedAssistantContent: "",
  completedExternalLinks: [],
  completedComparison: null,
  completedWebProducts: [],
  completedWebQuery: null,
  queuedMessages: [],
  isConnected: false,
  abortController: null,
  hasUnread: false,
  updatedAt: Date.now(),
  streamingError: null,
});

// ===== Completed Draft Acknowledgement =====

/**
 * `expected` içinden `actual`'da karşılığı bulunmayanları döner.
 *
 * Eşleşenler tüketiliyor: aynı bağlantı iki kez geldiyse history'de de iki kez
 * bulunmalı, tek kopya ikisini birden ack'lememeli.
 */
const missingItems = <TExpected, TActual>(
  expected: TExpected[],
  actual: TActual[],
  matches: (expectedItem: TExpected, actualItem: TActual) => boolean
): TExpected[] => {
  const unmatched = [...actual];
  return expected.filter((expectedItem) => {
    const index = unmatched.findIndex((actualItem) =>
      matches(expectedItem, actualItem)
    );
    if (index === -1) return true;
    unmatched.splice(index, 1);
    return false;
  });
};

/** Ack hesabının history mesajından ihtiyaç duyduğu alanlar. */
export interface AcknowledgementMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  images?: string[];
  externalLinks?: Array<{ url: string; label: string }>;
  webProducts?: WebSearchProduct[];
  webSearchQuery?: string;
  productComparison?: ProductComparisonSnapshot;
}

export interface CompletedDraftAcknowledgement {
  user: AcknowledgementMessage | undefined;
  /** Kullanıcı balonunun backend kopyası geldi mi? */
  userAcked: boolean;
  assistant: AcknowledgementMessage | undefined;
  /** Asistan cevabı **ve** stream'de gelen artefaktlarının hepsi geldi mi? */
  assistantAcked: boolean;
  /** Optimistic çift ve tüm artefaktları birlikte karşılandı mı? */
  complete: boolean;
  missing: {
    userImages: string[];
    externalLinks: StreamingExternalLink[];
    webProducts: WebSearchProduct[];
    webQuery: boolean;
    comparison: boolean;
  };
}

/**
 * Tamamlanmış optimistic draft'ın backend kopyası geldi mi?
 *
 * Eskiden yalnızca asistan mesajının id'si (ya da metni) aranıyordu. Metin
 * history'ye artefaktlardan önce düşebildiği için draft erken siliniyor, stream
 * sırasında görünen bağlantılar ve web ürün kartları ekrandan kayboluyordu —
 * kullanıcı açısından cevabın bir kısmının silinmesi demek.
 *
 * Ack artık stream'de ne geldiyse hepsinin history kopyasında bulunmasını şart
 * koşuyor. Eksik varsa draft ekranda kalır ve reconcile denemeleri sürer.
 *
 * ID'siz durum (initial_response hiç gelmedi) eski içerik-bazlı davranışını
 * koruyor: draft'ı ID yok diye sonsuza kadar tutmak, erken silmekten daha kötü.
 */
export const getCompletedDraftAcknowledgement = (
  backend: AcknowledgementMessage[],
  session: StreamingSession
): CompletedDraftAcknowledgement => {
  const hasAssistantDraft =
    session.completedAssistantContent !== "" ||
    session.completedExternalLinks.length > 0 ||
    session.completedWebProducts.length > 0 ||
    session.completedComparison !== null;

  if (!session.completedServerMessageId) {
    // Gösterilecek draft yoksa ack'li say; aksi halde boş bir draft oturumda
    // asılı kalır.
    if (!hasAssistantDraft && !session.completedUserMessage) {
      return {
        user: undefined,
        userAcked: true,
        assistant: undefined,
        assistantAcked: true,
        complete: true,
        missing: {
          userImages: [],
          externalLinks: [],
          webProducts: [],
          webQuery: false,
          comparison: false,
        },
      };
    }

    // İçerik bazlı fallback: backend aynı metni kaydettiyse çift birlikte kalkar.
    const contentAcked =
      hasAssistantDraft &&
      backend.some(
        (message) =>
          message.role === "assistant" &&
          message.content.trim() === session.completedAssistantContent.trim()
      );
    return {
      user: undefined,
      userAcked: contentAcked,
      assistant: undefined,
      assistantAcked: contentAcked,
      complete: contentAcked,
      missing: {
        userImages: [],
        externalLinks: [],
        webProducts: [],
        webQuery: false,
        comparison: false,
      },
    };
  }

  const user = backend.find(
    (message) =>
      message.id === `${session.completedServerMessageId}_user` &&
      message.role === "user"
  );
  const missingUserImages = missingItems(
    session.completedUserAttachments,
    user?.images ?? [],
    (left, right) => left === right
  );
  const userAcked =
    !session.completedUserMessage || !session.completedUserMessageId
      ? true
      : user !== undefined && missingUserImages.length === 0;

  const assistant = backend.find(
    (message) =>
      message.id === `${session.completedServerMessageId}_assistant` &&
      message.role === "assistant"
  );
  const missingExternalLinks = missingItems(
    session.completedExternalLinks,
    assistant?.externalLinks ?? [],
    (left, right) => left.url === right.url
  );
  const missingWebProducts = missingItems(
    session.completedWebProducts,
    assistant?.webProducts ?? [],
    (left, right) => left.externalId === right.externalId
  );
  // Sorgu metni tek bir alan; stream'de geldiyse history'de de olmalı.
  const missingWebQuery =
    session.completedWebQuery !== null && !assistant?.webSearchQuery;
  const missingComparison =
    session.completedComparison !== null && !assistant?.productComparison;

  const assistantAcked =
    !hasAssistantDraft ||
    (assistant !== undefined &&
      missingExternalLinks.length === 0 &&
      missingWebProducts.length === 0 &&
      !missingWebQuery &&
      !missingComparison);

  return {
    user,
    userAcked,
    assistant,
    assistantAcked,
    complete: userAcked && assistantAcked,
    missing: {
      userImages: missingUserImages,
      externalLinks: missingExternalLinks,
      webProducts: missingWebProducts,
      webQuery: missingWebQuery,
      comparison: missingComparison,
    },
  };
};

export interface StreamingStore {
  // ===== Per-chat Sessions =====
  sessionsByChat: Record<string, StreamingSession>;

  // ===== Model Suggestions (already per-chat) =====
  modelSuggestionsByChat: Record<string, ModelSuggestion[]>;
  modelListByChat: Record<string, ModelSuggestion[]>;

  // ===== Actions =====
  ensureSession: (chatKey: string) => void;
  getSession: (chatKey: string) => StreamingSession;

  /** Yeni bir tur başlatır ve o turun sahiplik token'ını (`streamId`) döner. */
  startStreaming: (
    chatKey: string,
    assistantMessageId: string,
    abortController: AbortController
  ) => string;
  setServerMessageId: (chatKey: string, serverMessageId: string) => void;
  updateStreamingContent: (chatKey: string, chunk: string) => void;
  addStatusMessage: (chatKey: string, status: StreamingStatus) => void;
  addReasoningMessage: (chatKey: string, reasoning: StreamingReasoning) => void;
  addExternalLink: (chatKey: string, link: StreamingExternalLink) => void;
  setComparison: (
    chatKey: string,
    comparison: ProductComparisonSnapshot
  ) => void;
  setWebProducts: (
    chatKey: string,
    products: WebSearchProduct[],
    query?: string | null
  ) => void;
  setPendingUserMessage: (
    chatKey: string,
    message: string | null,
    pendingUserMessageId?: string | null,
    attachments?: string[]
  ) => void;
  markChatSeen: (chatKey: string) => void;
  enqueueMessage: (
    chatKey: string,
    item: Omit<QueuedChatMessage, "id" | "timestamp">
  ) => void;
  dequeueMessage: (chatKey: string) => QueuedChatMessage | null;
  removeQueuedMessage: (chatKey: string, messageId: string) => void;
  completeStreaming: (
    chatKey: string,
    markUnread: boolean,
    streamId: string
  ) => void;
  clearCompletedDraft: (
    chatKey: string,
    expectedAssistantMessageId?: string
  ) => void;
  failStreaming: (
    chatKey: string,
    error: StreamingError,
    streamId: string,
    preserveLiveDraft?: boolean
  ) => void;
  setConnectionState: (
    chatKey: string,
    isConnected: boolean,
    streamId: string
  ) => void;
  resetSession: (chatKey: string) => void;
  /** Taslak oturumu, oluşan konuşmanın id'sine taşır. */
  renameSession: (fromKey: string, toKey: string) => void;
  abortStreaming: (chatKey: string, expectedStreamId?: string) => void;
  stopStreaming: (chatKey: string) => string | null;

  // Model suggestions
  setModelSuggestions: (models: ModelSuggestion[], chatId?: string) => void;
  setModelList: (models: ModelSuggestion[], chatId?: string) => void;
  getModelSuggestions: (chatId: string) => ModelSuggestion[];
  clearModelSuggestions: (chatId?: string) => void;
}

// ===== Streaming Store Implementation =====
export const useStreamingStore = create<StreamingStore>()(
  devtools(
    (set, get) => ({
      sessionsByChat: {},
      modelSuggestionsByChat: {},
      modelListByChat: {},

      ensureSession: (chatKey: string) => {
        const state = get();
        if (state.sessionsByChat[chatKey]) return;
        const nextSessions = pruneSessionsByChat({
          ...state.sessionsByChat,
          [chatKey]: createInitialSession(chatKey),
        });

        // Model suggestion GC: session'ı prune edilmiş chat'lerin
        // suggestion'larını da bırak (backend refetch'i gerektiğinde
        // mesajlar üzerinden geri getirir).
        const pruneSuggestionMap = (
          map: Record<string, ModelSuggestion[]>
        ): Record<string, ModelSuggestion[]> => {
          const next: Record<string, ModelSuggestion[]> = {};
          for (const key of Object.keys(map)) {
            if (key in nextSessions) next[key] = map[key];
          }
          return next;
        };

        set({
          sessionsByChat: nextSessions,
          modelSuggestionsByChat: pruneSuggestionMap(
            state.modelSuggestionsByChat
          ),
          modelListByChat: pruneSuggestionMap(state.modelListByChat),
        });
      },

      getSession: (chatKey: string) => {
        const state = get();
        return state.sessionsByChat[chatKey] || createInitialSession(chatKey);
      },

      startStreaming: (
        chatKey: string,
        assistantMessageId: string,
        abortController: AbortController
      ) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);

        // Aynı sohbette hâlâ açık bir tur varsa önce onu kapat. Aksi halde iki
        // okuma döngüsü aynı oturuma yazar ve cevaplar birbirine karışır.
        if (current.abortController) {
          try {
            current.abortController.abort();
          } catch {
            // ignore
          }
        }

        const streamId = streamingUtils.generateStreamId();

        const nextSessions = pruneSessionsByChat({
          ...state.sessionsByChat,
          [chatKey]: {
            ...current,
            streamId,
            status: "streaming",
            streamingMessageId: assistantMessageId,
            // Korelasyon bu tura ait: başarısız olmuş bir öncekinden devreden
            // serverMessageId bayattır ve yanlış mesajla eşleşmeye yol açar.
            // (Normal akışta completeStreaming bunu zaten null'a çekiyor.)
            serverMessageId: null,
            streamingContent: "",
            statusMessages: [],
            reasoningMessages: [],
            reasoningContent: "",
            externalLinks: [],
            comparison: null,
            webProducts: [],
            webSearchQuery: null,
            streamingError: null,
            isConnected: true,
            abortController,
            // While streaming, this chat is “active” in the sense of having new output.
            // unread dot should be set on completion if not currently viewed.
            updatedAt: Date.now(),
          },
        });
        set({ sessionsByChat: nextSessions });

        return streamId;
      },

      setServerMessageId: (chatKey: string, serverMessageId: string) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              serverMessageId,
              updatedAt: Date.now(),
            },
          },
        });
      },

      updateStreamingContent: (chatKey: string, chunk: string) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              streamingContent: current.streamingContent + chunk,
              updatedAt: Date.now(),
            },
          },
        });
      },

      addStatusMessage: (chatKey: string, status: StreamingStatus) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              statusMessages: [...current.statusMessages, status],
              updatedAt: Date.now(),
            },
          },
        });
      },

      addReasoningMessage: (chatKey: string, reasoning: StreamingReasoning) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              reasoningMessages: [...current.reasoningMessages, reasoning],
              reasoningContent: current.reasoningContent + reasoning.content,
              updatedAt: Date.now(),
            },
          },
        });
      },

      addExternalLink: (chatKey: string, link: StreamingExternalLink) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        // Aynı URL birden fazla tool/event tarafından adlandırılsa da tek
        // butondur. History transformer da URL bazında aynı kuralı uygular.
        if (current.externalLinks.some((item) => item.url === link.url)) return;
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              externalLinks: [...current.externalLinks, link],
              updatedAt: Date.now(),
            },
          },
        });
      },

      setComparison: (
        chatKey: string,
        comparison: ProductComparisonSnapshot
      ) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              comparison,
              updatedAt: Date.now(),
            },
          },
        });
      },

      setWebProducts: (
        chatKey: string,
        products: WebSearchProduct[],
        query?: string | null
      ) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              webProducts: products,
              webSearchQuery: query ?? null,
              updatedAt: Date.now(),
            },
          },
        });
      },

      setPendingUserMessage: (
        chatKey: string,
        message: string | null,
        pendingUserMessageId?: string | null,
        attachments?: string[]
      ) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              pendingUserMessage: message,
              pendingUserMessageId:
                pendingUserMessageId !== undefined
                  ? pendingUserMessageId
                  : current.pendingUserMessageId,
              // Mesaj temizleniyorsa attachment önizlemeleri de temizlensin
              pendingUserAttachments:
                message === null
                  ? []
                  : attachments ?? current.pendingUserAttachments,
              updatedAt: Date.now(),
            },
          },
        });
      },

      markChatSeen: (chatKey: string) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        const nextSessions = pruneSessionsByChat({
          ...state.sessionsByChat,
          [chatKey]: {
            ...current,
            hasUnread: false,
            updatedAt: Date.now(),
          },
        });
        set({ sessionsByChat: nextSessions });
      },

      enqueueMessage: (
        chatKey: string,
        item: Omit<QueuedChatMessage, "id" | "timestamp">
      ) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        const queuedItem: QueuedChatMessage = {
          ...item,
          id: `queued_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          timestamp: Date.now(),
        };
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              queuedMessages: [...current.queuedMessages, queuedItem],
              updatedAt: Date.now(),
            },
          },
        });
      },

      dequeueMessage: (chatKey: string) => {
        const state = get();
        const current = state.sessionsByChat[chatKey];
        if (!current || current.queuedMessages.length === 0) return null;
        const [next, ...rest] = current.queuedMessages;
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              queuedMessages: rest,
              updatedAt: Date.now(),
            },
          },
        });
        return next;
      },

      removeQueuedMessage: (chatKey: string, messageId: string) => {
        const state = get();
        const current = state.sessionsByChat[chatKey];
        if (!current) return;
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              queuedMessages: current.queuedMessages.filter(
                (queued) => queued.id !== messageId
              ),
              updatedAt: Date.now(),
            },
          },
        });
      },

      /**
       * Stream'i bitirir ve canlı draft'ı tamamlanmış draft'a taşır.
       *
       * **Bilerek idempotent.** Aynı tur için birden fazla kez çağrılabiliyor:
       * `[DONE]` bir hatadan sonra da gelebiliyor ve stream completion event'i
       * olmadan kapandığında sonlandırma dalı da devreye giriyor. İlk çağrıdan
       * sonrakiler no-op olmak zorunda — çünkü ikinci çağrıda canlı draft çoktan
       * boşalmış olur ve onu tamamlanmış draft'ın üstüne kopyalamak
       * `completedAssistantContent`'i siler, cevap biter bitmez paneli boşaltır.
       * `error` da aynı sebeple terminal: sonradan gelen bir `[DONE]` hatayı
       * sahte bir tamamlanmayla ezmemeli.
       */
      completeStreaming: (
        chatKey: string,
        markUnread: boolean,
        streamId: string
      ) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);

        // Sahibi değil: eski bir kuşağın geç gelen sonlandırması, o sırada
        // sürmekte olan yeni turu bitiremez.
        if (current.streamId !== streamId) return;

        // Tur zaten sonlanmış: yalnızca okunmadı işaretini güncelle, draft'a dokunma.
        if (current.status === "completed" || current.status === "error") {
          if (!markUnread || current.hasUnread) return;
          set({
            sessionsByChat: {
              ...state.sessionsByChat,
              [chatKey]: { ...current, hasUnread: true, updatedAt: Date.now() },
            },
          });
          return;
        }

        const nextSessions = pruneSessionsByChat({
          ...state.sessionsByChat,
          [chatKey]: {
            ...current,
            status: "completed",
            streamingMessageId: null,
            isConnected: false,
            // Move current optimistic pair into completed draft (backend sync bekleniyor)
            completedServerMessageId: current.serverMessageId,
            completedUserMessageId: current.pendingUserMessageId,
            completedUserMessage: current.pendingUserMessage,
            completedUserAttachments: current.pendingUserAttachments,
            completedAssistantMessageId: current.streamingMessageId,
            completedAssistantContent: current.streamingContent,
            completedExternalLinks: current.externalLinks,
            completedComparison: current.comparison,
            completedWebProducts: current.webProducts,
            completedWebQuery: current.webSearchQuery,

            // Clear active draft (new messages can start without losing completed one)
            serverMessageId: null,
            pendingUserMessageId: null,
            pendingUserMessage: null,
            pendingUserAttachments: [],
            streamingContent: "",
            statusMessages: [],
            reasoningMessages: [],
            reasoningContent: "",
            externalLinks: [],
            comparison: null,
            webProducts: [],
            webSearchQuery: null,

            hasUnread: markUnread ? true : current.hasUnread,
            abortController: null,
            updatedAt: Date.now(),
          },
        });
        set({ sessionsByChat: nextSessions });
      },

      clearCompletedDraft: (
        chatKey: string,
        expectedAssistantMessageId?: string
      ) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);

        // Beklenen draft değilse dokunma: bu arada yeni bir tur tamamlanıp
        // kendi draft'ını yazmış olabilir ve eski turun ack'i onu silmemeli.
        if (
          expectedAssistantMessageId !== undefined &&
          current.completedAssistantMessageId !== expectedAssistantMessageId
        ) {
          return;
        }

        const nextSessions = pruneSessionsByChat({
          ...state.sessionsByChat,
          [chatKey]: {
            ...current,
            completedServerMessageId: null,
            completedUserMessageId: null,
            completedUserMessage: null,
            completedUserAttachments: [],
            completedAssistantMessageId: null,
            completedAssistantContent: "",
            completedExternalLinks: [],
            completedComparison: null,
            completedWebProducts: [],
            completedWebQuery: null,
            // History sync sonrası live web cards history mesajına geçer
            externalLinks: [],
            comparison: null,
            webProducts: [],
            webSearchQuery: null,
            updatedAt: Date.now(),
          },
        });
        set({ sessionsByChat: nextSessions });
      },

      failStreaming: (
        chatKey: string,
        error: StreamingError,
        streamId: string,
        preserveLiveDraft: boolean = false
      ) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);

        // Sahibi değil: eski bir kuşağın hatası yeni turu bozmaz.
        if (current.streamId !== streamId) return;

        const shouldPreserveDraft =
          preserveLiveDraft && current.status === "streaming";

        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              status: "error",
              streamingMessageId: null,
              isConnected: false,
              streamingError: error,
              abortController: null,
              ...(shouldPreserveDraft
                ? {
                    completedServerMessageId: current.serverMessageId,
                    completedUserMessageId: current.pendingUserMessageId,
                    completedUserMessage: current.pendingUserMessage,
                    completedUserAttachments: current.pendingUserAttachments,
                    completedAssistantMessageId: current.streamingMessageId,
                    completedAssistantContent: current.streamingContent,
                    completedExternalLinks: current.externalLinks,
                    completedComparison: current.comparison,
                    completedWebProducts: current.webProducts,
                    completedWebQuery: current.webSearchQuery,
                    serverMessageId: null,
                    pendingUserMessageId: null,
                    pendingUserMessage: null,
                    pendingUserAttachments: [],
                    streamingContent: "",
                    statusMessages: [],
                    reasoningMessages: [],
                    reasoningContent: "",
                    externalLinks: [],
                    comparison: null,
                    webProducts: [],
                    webSearchQuery: null,
                  }
                : {}),
              updatedAt: Date.now(),
            },
          },
        });
      },

      setConnectionState: (
        chatKey: string,
        isConnected: boolean,
        streamId: string
      ) => {
        const state = get();
        const current =
          state.sessionsByChat[chatKey] || createInitialSession(chatKey);
        if (current.streamId !== streamId) return;
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: { ...current, isConnected, updatedAt: Date.now() },
          },
        });
      },

      renameSession: (fromKey, toKey) => {
        if (fromKey === toKey) return;
        const state = get();
        const session = state.sessionsByChat[fromKey];
        if (!session) return;
        const nextSessions = { ...state.sessionsByChat };
        delete nextSessions[fromKey];
        nextSessions[toKey] = { ...session, chatKey: toKey };
        const moveMap = (map: Record<string, ModelSuggestion[]>) => {
          if (!(fromKey in map)) return map;
          const next = { ...map, [toKey]: map[fromKey] };
          delete next[fromKey];
          return next;
        };
        set({
          sessionsByChat: nextSessions,
          modelSuggestionsByChat: moveMap(state.modelSuggestionsByChat),
          modelListByChat: moveMap(state.modelListByChat),
        });
      },

      resetSession: (chatKey: string) => {
        const state = get();
        // Model suggestions are stored separately and should persist.
        // Abort any in-flight streaming for safety.
        const current = state.sessionsByChat[chatKey];
        if (current?.abortController) {
          try {
            current.abortController.abort();
          } catch {
            // ignore
          }
        }
        const next = createInitialSession(chatKey);
        const nextSessions = pruneSessionsByChat({
          ...state.sessionsByChat,
          [chatKey]: next,
        });
        set({ sessionsByChat: nextSessions });
      },

      abortStreaming: (chatKey: string, expectedStreamId?: string) => {
        const state = get();
        const current = state.sessionsByChat[chatKey];
        if (!current?.abortController) return;
        if (
          expectedStreamId !== undefined &&
          current.streamId !== expectedStreamId
        ) {
          return;
        }
        try {
          current.abortController.abort();
        } catch {
          // ignore
        }
        set({
          sessionsByChat: {
            ...state.sessionsByChat,
            [chatKey]: {
              ...current,
              // Sahiplik bırakılıyor: durdurulan turun geç gelen sonlandırması
              // artık bu oturuma yazamaz.
              streamId: null,
              status: "idle",
              isConnected: false,
              abortController: null,
              updatedAt: Date.now(),
            },
          },
        });
      },

      stopStreaming: (chatKey: string) => {
        const current = get().sessionsByChat[chatKey];
        if (!current?.streamId || !current.abortController) return null;

        const { streamId, abortController } = current;

        // Önce canlı optimistic çifti completed draft'a taşı. Abort önce
        // yapılırsa catch kolu sahipliği bırakıp kısmi cevabı görünmez yapar.
        get().completeStreaming(chatKey, false, streamId);

        try {
          abortController.abort();
        } catch {
          // ignore
        }

        const completed = get().sessionsByChat[chatKey];
        if (completed?.streamId === streamId) {
          set({
            sessionsByChat: {
              ...get().sessionsByChat,
              [chatKey]: {
                ...completed,
                streamId: null,
                isConnected: false,
                abortController: null,
                updatedAt: Date.now(),
              },
            },
          });
        }

        return streamId;
      },

      setModelSuggestions: (models: ModelSuggestion[], chatId?: string) => {
        const state = get();
        const targetChatId = chatId;

        if (!targetChatId) {
          return;
        }

        // ✅ Chat bazında kaydet
        const updatedSuggestionsByChat = {
          ...state.modelSuggestionsByChat,
          [targetChatId]: models,
        };

        set({
          modelSuggestionsByChat: updatedSuggestionsByChat,
        });
      },

      setModelList: (models: ModelSuggestion[], chatId?: string) => {
        const state = get();
        const targetChatId = chatId;

        if (!targetChatId) {
          return;
        }

        // ✅ Chat bazında kaydet
        const updatedModelListByChat = {
          ...state.modelListByChat,
          [targetChatId]: models,
        };

        set({
          modelListByChat: updatedModelListByChat,
        });
      },

      getModelSuggestions: (chatId: string) => {
        const state = get();
        return state.modelSuggestionsByChat[chatId] || [];
      },

      clearModelSuggestions: (chatId?: string) => {
        const state = get();

        if (chatId) {
          // ✅ Sadece belirtilen chat'in suggestions'larını temizle
          const updatedSuggestionsByChat = { ...state.modelSuggestionsByChat };
          const updatedModelListByChat = { ...state.modelListByChat };
          delete updatedSuggestionsByChat[chatId];
          delete updatedModelListByChat[chatId];

          set({
            modelSuggestionsByChat: updatedSuggestionsByChat,
            modelListByChat: updatedModelListByChat,
          });
        } else {
          // ✅ Tüm suggestions'ları temizle
          set({
            modelSuggestionsByChat: {},
            modelListByChat: {},
          });
        }
      },
    }),
    {
      name: "streaming-store",
      // DevTools options
      serialize: {
        // Exclude performance-sensitive data from devtools
        replacer: (key: string, value: any) => {
          if (key === "abortController") return undefined;
          return value;
        },
      },
    }
  )
);

// ===== Convenience selectors =====
export const getChatKey = (chatId: string | null | undefined): string =>
  chatId && chatId.trim() !== "" ? chatId : PLAYGROUND_STREAM_KEY;

// ===== Utility Functions =====
export const streamingUtils = {
  // Generate unique message ID
  generateMessageId: (): string => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },

  // Tur sahiplik token'ı — aynı milisaniyede iki tur başlasa da çakışmasın
  // diye zaman damgasına rastgele bir kuyruk ekleniyor.
  generateStreamId: (): string => {
    return `stream_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  },
};

export default useStreamingStore;
